/**
 * GlobalDocumentTypeFormModal — panel ITE, „Globalne typy dokumentów"
 * (plan `glimmering-bouncing-hamster.md` §„Implementacja — frontend" F3).
 *
 * Modal create/edit dla typu globalnego. Klasa jest ustalona z kontekstu strony
 * (segmentacja 7 klas) i nieedytowalna — pokazana tylko jako etykieta.
 *
 * Kod jest edytowalny wyłącznie dla szkiców (`status==='draft'`) — po publikacji
 * backend odrzuca zmianę kodu (409 CODE_IMMUTABLE), więc pole jest `disabled`
 * z podpowiedzią (`form.codeImmutableHint`) zamiast pozwalać na próbę zapisu.
 *
 * Walidacja klienta lustrzana do `DocumentTypeFormModal` (tenant): code/name
 * wymagane + limity długości, opis/maska opcjonalne z limitami, maska numeracji
 * przez `numberingMaskError` (współdzielone z `NumberingMaskEditor`/tenantem).
 *
 * Domyślny layout formularza (przypisanie centralne z ADM) — Etap 3 (T3.5, `formViewSlug`/`formViews` props):
 * picker widoków FORMULARZA z biblioteki ADM (`kartoteka_views`) danej klasy dokumentu
 * (rodzic fetchuje przez `useKartotekaSystemViews`, keyowane slugiem kartoteki klasy).
 * Pusty wybór = `null` (brak centralnego wskazania — rezolucja `/layouts/active` krok 3
 * spada do kaskady, T3.6). `formViewsUnavailable` — lustro idiomu `layoutsUnavailable`
 * z tenantowego `DocumentTypeFormModal`: fetch zawiódł → pole disabled + neutralny hint
 * zamiast crashować/chować pole. Prefill w edit działa nawet gdy wskazany slug nie ma
 * już odpowiednika w bieżącej liście `formViews` (widok usunięty z biblioteki) —
 * syntetyczna opcja „niedostępny" (lustro analogicznego fixu w tenantowym pickerze T1.8).
 *
 * Domyślny proces przez szablon (ADR-0070, `defaultProcessTemplateId`/`templates` props):
 * picker OPUBLIKOWANYCH szablonów procesu z katalogu ADM (cel `crm_core.documents`),
 * NIE proces tenantowy bezpośrednio. Rodzic fetchuje listę raz (cel stały dla
 * wszystkich klas dokumentu, w przeciwieństwie do `formViews` keyowanych klasą).
 * `null` = brak centralnego wskazania. Ten sam idiom co `formViewSlug`: pole
 * disabled + hint gdy fetch zawiódł (`templatesUnavailable`), syntetyczna opcja
 * „niedostępny" gdy wskazany szablon zniknął z bieżącej listy.
 */

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { DocumentClass, DocumentNumberingReset } from '@saas/shared';
import {
  DOCUMENT_CORE_DATE_FIELDS,
  isHrAbsenceDocumentClass,
  normalizeNumberingBaseSlug,
} from '@saas/shared';

import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import { NumberingMaskEditor } from '@/components/documents/NumberingMaskEditor';
import type { NumberingBaseFieldOption } from '@/components/documents/useNumberingBaseFieldOptions';
import { Dropdown } from '@/components/Dropdown';
import { FormField } from '@/components/FormField';
import { InfoHint } from '@/components/InfoHint';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { Textarea } from '@/components/Textarea';
import { numberingMaskError } from '@/lib/document-number-preview';

export interface GlobalDocumentTypeFormValues {
  code: string;
  name: string;
  description: string;
  numberingFormat: string;
  numberingBaseField: string;
  numberingReset: DocumentNumberingReset;
  sortOrder: string;
  /**
   * Domyślny layout formularza (T3.5) — slug widoku FORM z biblioteki ADM
   * `kartoteka_views`, kartoteka klasy dokumentu. `null` = brak centralnego wskazania.
   */
  formViewSlug: string | null;
  /**
   * Domyślny proces przez szablon (ADR-0070) — UUID opublikowanego szablonu
   * procesu z katalogu ADM (cel `crm_core.documents`). `null` = brak centralnego
   * wskazania.
   */
  defaultProcessTemplateId: string | null;
  /**
   * Flaga „ten typ to wniosek o nieobecność" — typ DOMYŚLNY dla klientów, którzy nie wybrali
   * własnego w Ustawieniach → „Typy dokumentów" (zasila kadrowy kafelek „Urlopy" i skrót
   * „Nieobecności"). Co najwyżej JEDEN oznaczony typ w całej platformie (kolizja → 409 na
   * zapisie); tylko klasy specjalne/internal (inne → 400).
   */
  isHrAbsenceSource: boolean;
}

/** Opcja pickera widoku formularza (T3.5) — widoki FORM biblioteki kartoteki danej klasy. */
export interface FormViewOption {
  slug: string;
  name: string;
}

/** Opcja pickera domyślnego procesu (ADR-0070) — opublikowane szablony procesu celu `crm_core.documents`. */
export interface DefaultProcessTemplateOption {
  id: string;
  name: string;
}

interface Props {
  open: boolean;
  mode: 'create' | 'edit';
  /** Klasa dokumentu — z kontekstu strony (segmentacja), nieedytowalna w modalu. */
  documentClass: DocumentClass;
  /**
   * Opcje pola daty numeracji budowane w rodzicu (`useNumberingBaseFieldOptions`,
   * `includeExt=false` — typy globalne nie mają pól dodatkowych tenanta). Pominięte →
   * komponent liczy CORE-pola danej klasy sam, bez query (żeby ten „pure controlled"
   * komponent — patrz kolokowany test — nie zaczął wymagać `QueryClientProvider` tylko
   * dla fallbacku); poprawne etykiety per klasa, NIE hardkodowany `document_date`
   * (błędny dla klas faktur, których pole domyślne to `issue_date`).
   */
  baseFieldOptions?: NumberingBaseFieldOption[];
  initialValues: GlobalDocumentTypeFormValues;
  /** Kod jest zamrożony gdy edytowany typ ma status inny niż 'draft'. */
  codeLocked?: boolean;
  saving?: boolean;
  /** Błędy pól zmapowane z odpowiedzi 409/422 backendu (np. code: 'Ten kod jest już zajęty…'). */
  serverErrors?: Record<string, string>;
  /**
   * Widoki FORM biblioteki kartoteki tej klasy dokumentu (T3.5) — do pickera „Domyślny
   * layout". Podawane z rodzica (React Query, keyowane slugiem kartoteki).
   */
  formViews?: FormViewOption[];
  /**
   * True gdy pobranie listy widoków zawiodło — pole pozostaje widoczne, ale wyłączone
   * (disabled) z neutralną podpowiedzią zamiast crashować albo chować pole.
   */
  formViewsUnavailable?: boolean;
  /**
   * Opublikowane szablony procesu celu `crm_core.documents` (ADR-0070) — do pickera
   * „Domyślny proces". Podawane z rodzica (React Query), stałe dla wszystkich klas.
   */
  templates?: DefaultProcessTemplateOption[];
  /**
   * True gdy pobranie listy szablonów zawiodło — pole pozostaje widoczne, ale
   * wyłączone (disabled) z neutralną podpowiedzią (lustro `formViewsUnavailable`).
   */
  templatesUnavailable?: boolean;
  /**
   * Tryb osadzony — renderuje treść bez powłoki overlay i bez nagłówka (powłokę i tytuł
   * daje rodzic, np. zakładka edytora typu). `open` nadal steruje montażem/prefillem.
   */
  embedded?: boolean;
  /**
   * Tryb osadzony: zgłasza rodzicowi, czy formularz ma niezapisane zmiany (rodzic ma powłokę
   * okna i to on pyta „Porzucić zmiany?"). Przy odmontowaniu zgłasza `false`. Okno samodzielne
   * pyta samo (`guardUnsaved`) i tego nie używa. Callback nie musi być stabilny (formularz trzyma
   * go w `ref`).
   */
  onDirtyChange?: (dirty: boolean) => void;
  onSubmit: (values: GlobalDocumentTypeFormValues) => void;
  onClose: () => void;
}

export function emptyGlobalDocumentTypeFormValues(): GlobalDocumentTypeFormValues {
  return {
    code: '',
    name: '',
    description: '',
    numberingFormat: '',
    numberingBaseField: 'document_date',
    numberingReset: 'yearly',
    sortOrder: '',
    formViewSlug: null,
    defaultProcessTemplateId: null,
    isHrAbsenceSource: false,
  };
}

export function GlobalDocumentTypeFormModal({
  open,
  mode,
  documentClass,
  baseFieldOptions,
  initialValues,
  codeLocked,
  saving,
  serverErrors,
  formViews = [],
  formViewsUnavailable = false,
  templates = [],
  templatesUnavailable = false,
  embedded = false,
  onDirtyChange,
  onSubmit,
  onClose,
}: Props) {
  const { t } = useTranslation();
  const [code, setCode] = useState(initialValues.code);
  const [name, setName] = useState(initialValues.name);
  const [description, setDescription] = useState(initialValues.description);
  const [numberingFormat, setNumberingFormat] = useState(initialValues.numberingFormat);
  const [numberingBaseField, setNumberingBaseField] = useState(() =>
    normalizeNumberingBaseSlug(documentClass, initialValues.numberingBaseField),
  );
  const [numberingReset, setNumberingReset] = useState<DocumentNumberingReset>(
    initialValues.numberingReset,
  );
  const [sortOrder, setSortOrder] = useState(initialValues.sortOrder);
  const [formViewSlug, setFormViewSlug] = useState(initialValues.formViewSlug);
  const [defaultProcessTemplateId, setDefaultProcessTemplateId] = useState(
    initialValues.defaultProcessTemplateId,
  );
  const [isHrAbsenceSource, setIsHrAbsenceSource] = useState(initialValues.isHrAbsenceSource);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Brak zmian względem stanu wczytanego przy otwarciu → PUT wysłałby pusty obiekt, który
  // backend odrzuca niezrozumiałym błędem (walidator wymaga co najmniej jednego pola). Blokujemy
  // zapis u źródła zamiast pozwalać na zapytanie skazane na porażkę.
  //
  // `numberingBaseField` porównujemy do znormalizowanej wartości początkowej (ta sama funkcja
  // co przy inicjalizacji stanu) — surowy `initialValues.numberingBaseField` bywa nieaktualny dla
  // bieżącej klasy dokumentu (np. 'document_date' gdy klasa go nie ma), co bez normalizacji
  // dawałoby fałszywe „zmieniono" zaraz po otwarciu.
  const normalizedInitialNumberingBaseField = useMemo(
    () => normalizeNumberingBaseSlug(documentClass, initialValues.numberingBaseField),
    [documentClass, initialValues.numberingBaseField],
  );
  const isDirty = useMemo(
    () =>
      code !== initialValues.code ||
      name !== initialValues.name ||
      description !== initialValues.description ||
      numberingFormat !== initialValues.numberingFormat ||
      numberingBaseField !== normalizedInitialNumberingBaseField ||
      numberingReset !== initialValues.numberingReset ||
      sortOrder !== initialValues.sortOrder ||
      formViewSlug !== initialValues.formViewSlug ||
      defaultProcessTemplateId !== initialValues.defaultProcessTemplateId ||
      isHrAbsenceSource !== initialValues.isHrAbsenceSource,
    [
      code,
      name,
      description,
      numberingFormat,
      numberingBaseField,
      normalizedInitialNumberingBaseField,
      numberingReset,
      sortOrder,
      formViewSlug,
      defaultProcessTemplateId,
      isHrAbsenceSource,
      initialValues,
    ],
  );

  // Fallback gdy rodzic nie podał `baseFieldOptions` — core-pola daty tej klasy z i18n
  // etykietami, liczone LOKALNIE (bez `useNumberingBaseFieldOptions`/query — panel ITE
  // nie ma pól dodatkowych tenanta, `includeExt` byłby zawsze `false`, więc nie ma sensu
  // wciągać tu React Query tylko dla samych core-pól).
  const fallbackBaseFieldOptions = useMemo<NumberingBaseFieldOption[]>(
    () =>
      DOCUMENT_CORE_DATE_FIELDS[documentClass].map((f) => ({
        value: f.slug,
        label: t(f.labelKey),
      })),
    [documentClass, t],
  );
  const effectiveBaseFieldOptions = baseFieldOptions ?? fallbackBaseFieldOptions;

  useEffect(() => {
    if (open) {
      setCode(initialValues.code);
      setName(initialValues.name);
      setDescription(initialValues.description);
      setNumberingFormat(initialValues.numberingFormat);
      setNumberingBaseField(
        normalizeNumberingBaseSlug(documentClass, initialValues.numberingBaseField),
      );
      setNumberingReset(initialValues.numberingReset);
      setSortOrder(initialValues.sortOrder);
      setFormViewSlug(initialValues.formViewSlug);
      setDefaultProcessTemplateId(initialValues.defaultProcessTemplateId);
      setIsHrAbsenceSource(initialValues.isHrAbsenceSource);
      setErrors({});
    }
  }, [open, initialValues, documentClass]);

  // Błędy zwrócone przez rodzica (409/422 mapowane po submit) mieszają się z
  // lokalnym stanem walidacji — pokazujemy je dopóki user nie zmieni pola.
  useEffect(() => {
    if (serverErrors) {
      setErrors((prev) => ({ ...prev, ...serverErrors }));
    }
  }, [serverErrors]);

  // Rodzic (powłoka okna) dowiaduje się o zmianach; po odmontowaniu formularza (przełączenie
  // zakładki, zamknięcie okna) stan wraca do „bez zmian". Callback trzymamy w `ref`, więc nie musi
  // być stabilny: efekty zależą tylko od samej wartości, a sprzątanie zgłasza `false` raz.
  const onDirtyChangeRef = useRef(onDirtyChange);
  useLayoutEffect(() => {
    onDirtyChangeRef.current = onDirtyChange;
  });
  // Osadzony formularz „zamknięty" (`open=false`) nie ma niczego do zapisania.
  const reportedDirty = embedded && !open ? false : isDirty;
  useEffect(() => {
    onDirtyChangeRef.current?.(reportedDirty);
  }, [reportedDirty]);
  useEffect(() => () => onDirtyChangeRef.current?.(false), []);

  // Tryb osadzony nie ma powłoki okna — o montażu decyduje rodzic przez `open`; okno samodzielne
  // (`Modal`) montuje treść samo tylko gdy otwarte.
  if (embedded && !open) return null;

  function validate(): boolean {
    const next: Record<string, string> = {};
    if (!code.trim()) {
      next.code = t('adminDocumentTypes.form.errors.codeRequired');
    } else if (code.trim().length > 30) {
      next.code = t('adminDocumentTypes.form.errors.codeLength');
    }
    if (!name.trim()) {
      next.name = t('adminDocumentTypes.form.errors.nameRequired');
    } else if (name.trim().length > 100) {
      next.name = t('adminDocumentTypes.form.errors.nameLength');
    }
    if (description.length > 500) {
      next.description = t('adminDocumentTypes.form.errors.descriptionLength');
    }
    if (numberingFormat.length > 50) {
      next.numberingFormat = t('adminDocumentTypes.form.errors.numberingFormatLength');
    } else if (numberingFormat.trim()) {
      const maskErr = numberingMaskError(numberingFormat);
      if (maskErr === 'invalid') {
        next.numberingFormat = t('adminDocumentTypes.form.errors.numberingFormatInvalid');
      } else if (maskErr === 'noCounter') {
        next.numberingFormat = t('adminDocumentTypes.form.errors.numberingFormatNoCounter');
      }
    }
    if (sortOrder !== '' && !/^-?\d+$/.test(sortOrder.trim())) {
      next.sortOrder = t('adminDocumentTypes.form.errors.sortOrderInvalid');
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  function handleSubmit() {
    if (!validate()) return;
    onSubmit({
      code: code.trim(),
      name: name.trim(),
      description: description.trim(),
      numberingFormat: numberingFormat.trim(),
      numberingBaseField,
      numberingReset,
      sortOrder: sortOrder.trim(),
      formViewSlug,
      defaultProcessTemplateId,
      isHrAbsenceSource,
    });
  }

  const titleKey =
    mode === 'create' ? 'adminDocumentTypes.form.titleCreate' : 'adminDocumentTypes.form.titleEdit';
  const isCodeDisabled = mode === 'edit' && codeLocked === true;

  // Pola formularza — te same w oknie samodzielnym (w `Modal`) i osadzonym (w zakładce rodzica).
  const formFields = (
    <>
      <FormField label={t('adminDocumentTypes.form.class')}>
        <Input
          value={t(`adminDocumentTypes.classes.${documentClass}`)}
          disabled
          readOnly
          data-testid="global-document-type-form-class-readonly"
        />
      </FormField>
      <FormField
        label={t('adminDocumentTypes.form.code')}
        error={errors.code}
        hint={isCodeDisabled ? t('adminDocumentTypes.form.codeImmutableHint') : undefined}
      >
        <Input
          value={code}
          onChange={(e) => {
            setCode(e.target.value);
            setErrors((prev) => ({ ...prev, code: '' }));
          }}
          disabled={isCodeDisabled}
          placeholder={t('adminDocumentTypes.form.codePlaceholder')}
          data-testid="global-document-type-form-code"
        />
      </FormField>
      <FormField label={t('adminDocumentTypes.form.name')} error={errors.name}>
        <Input
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            setErrors((prev) => ({ ...prev, name: '' }));
          }}
          placeholder={t('adminDocumentTypes.form.namePlaceholder')}
          data-testid="global-document-type-form-name"
        />
      </FormField>
      <FormField label={t('adminDocumentTypes.form.description')} error={errors.description}>
        <Textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={t('adminDocumentTypes.form.descriptionPlaceholder')}
          rows={2}
          data-testid="global-document-type-form-description"
        />
      </FormField>
      <NumberingMaskEditor
        code={code}
        numberingFormat={numberingFormat}
        numberingBaseField={numberingBaseField}
        baseFieldOptions={effectiveBaseFieldOptions}
        numberingReset={numberingReset}
        onFormatChange={setNumberingFormat}
        onBaseFieldChange={setNumberingBaseField}
        onResetChange={setNumberingReset}
        formatError={errors.numberingFormat}
        testIdPrefix="global-document-type-form"
      />
      <FormField label={t('adminDocumentTypes.form.sortOrder')} error={errors.sortOrder}>
        <Input
          value={sortOrder}
          onChange={(e) => setSortOrder(e.target.value)}
          placeholder={t('adminDocumentTypes.form.sortOrderPlaceholder')}
          data-testid="global-document-type-form-sort-order"
        />
        <InfoHint>{t('common.sortOrderInfo')}</InfoHint>
      </FormField>
      <FormField
        label={t('adminDocumentTypes.form.formViewSlug')}
        hint={
          formViewsUnavailable
            ? t('adminDocumentTypes.form.formViewSlugUnavailableHint')
            : undefined
        }
      >
        <Dropdown
          value={formViewSlug ?? ''}
          onChange={(v) => setFormViewSlug(v || null)}
          disabled={formViewsUnavailable}
          data-testid="global-document-type-form-form-view-slug"
          options={[
            { value: '', label: t('adminDocumentTypes.form.formViewSlugNone') },
            ...formViews.map((v) => ({ value: v.slug, label: v.name })),
            // Widok wskazany, którego nie ma w bieżącej liście (usunięty z biblioteki) —
            // bez tej opcji dropdown fałszywie pokazałby "brak wskazania", mimo że stan
            // trzyma realny slug (lustro analogicznego fixu tenantowego pickera T1.8).
            ...(formViewSlug !== null && !formViews.some((v) => v.slug === formViewSlug)
              ? [
                  {
                    value: formViewSlug,
                    label: t('adminDocumentTypes.form.formViewSlugMissing'),
                  },
                ]
              : []),
          ]}
        />
      </FormField>
      <FormField
        label={t('adminDocumentTypes.form.defaultProcessTemplateId')}
        hint={
          templatesUnavailable
            ? t('adminDocumentTypes.form.defaultProcessTemplateUnavailableHint')
            : undefined
        }
      >
        <Dropdown
          value={defaultProcessTemplateId ?? ''}
          onChange={(v) => setDefaultProcessTemplateId(v || null)}
          disabled={templatesUnavailable}
          data-testid="global-document-type-form-default-process-template"
          options={[
            { value: '', label: t('adminDocumentTypes.form.defaultProcessTemplateNone') },
            ...templates.map((tpl) => ({ value: tpl.id, label: tpl.name })),
            // Szablon wskazany, którego nie ma w bieżącej liście (wycofany/skasowany) —
            // lustro analogicznej syntetycznej opcji dla `formViewSlug` powyżej.
            ...(defaultProcessTemplateId !== null &&
            !templates.some((tpl) => tpl.id === defaultProcessTemplateId)
              ? [
                  {
                    value: defaultProcessTemplateId,
                    label: t('adminDocumentTypes.form.defaultProcessTemplateMissing'),
                  },
                ]
              : []),
          ]}
        />
      </FormField>
      {/* Flaga HR: „ten typ to wnioski o nieobecność" = typ DOMYŚLNY dla klientów, którzy nie
          wybrali własnego. Co najwyżej jeden oznaczony typ w całej platformie — kolizję (409)
          mapuje rodzic na czytelny komunikat. Tylko klasy specjalne/internal (serwer odrzuca
          inne 400) — wyjątek: typ innej klasy JUŻ oznaczony (dane sprzed ograniczenia) widzi
          przełącznik, żeby dało się zdjąć oznaczenie. */}
      {(isHrAbsenceDocumentClass(documentClass) || initialValues.isHrAbsenceSource) && (
        <Checkbox
          label={t('adminDocumentTypes.form.hrAbsenceSourceLabel')}
          hint={t('adminDocumentTypes.form.hrAbsenceSourceHint')}
          checked={isHrAbsenceSource}
          onChange={(e) => setIsHrAbsenceSource(e.target.checked)}
          data-testid="global-document-type-form-hr-absence-source"
        />
      )}
    </>
  );

  const footerButtons = (
    <>
      <Button
        variant="ghost"
        onClick={onClose}
        disabled={saving}
        data-testid="global-document-type-form-cancel"
      >
        {t('common.cancel')}
      </Button>
      <Button
        variant="primary"
        onClick={handleSubmit}
        loading={saving}
        disabled={mode === 'edit' && !isDirty}
        title={mode === 'edit' && !isDirty ? t('adminDocumentTypes.form.noChangesHint') : undefined}
        data-testid="global-document-type-form-submit"
      >
        {mode === 'create' ? t('common.create') : t('common.save')}
      </Button>
    </>
  );

  // Tryb osadzony: bez powłoki okna i bez nagłówka — powłokę i tytuł daje rodzic (zakładka
  // edytora typu), a zamknięciem steruje on sam.
  if (embedded) {
    return (
      <div data-testid="global-document-type-form-modal">
        <div className="space-y-3 p-4">{formFields}</div>
        <footer className="flex shrink-0 items-center justify-end gap-2 border-t border-border-primary px-4 py-3">
          {footerButtons}
        </footer>
      </div>
    );
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t(titleKey)}
      size="sm"
      testId="global-document-type-form-modal"
      guardUnsaved
      dirty={isDirty}
      // W trakcie zapisu tło i Esc nie zamykają okna („Anuluj" jest wyłączone; X w nagłówku zostaje).
      closeOnBackdrop={!saving}
      closeOnEsc={!saving}
      footer={footerButtons}
    >
      <div className="space-y-3">{formFields}</div>
    </Modal>
  );
}
